import './styles/index.css';
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App from './App';

const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

/* The whole mount is browser-gated because this module is reachable from the
 * BUILD-TIME prerender entry: both entries share one chunk graph, so importing
 * the app's shared chunk in Node evaluates this file's top level. Unguarded,
 * that is a "document is not defined" ReferenceError that fails the build. The
 * guard is the honest statement anyway — mounting means nothing outside a
 * browser.
 *
 * Every route ships prerendered markup, and createRoot DISCARDS existing
 * container children — it would blank the real content and re-render it, a
 * visible flash on every cold load. So: hydrate when there is markup to adopt.
 *
 * Gated on a POSITIVE marker stamped by postBuild.ts, not on
 * rootElement.firstChild: that would depend on index.html having no
 * whitespace inside <div id="root">, and a reformat would silently turn the
 * dev server's empty container into a hydration error on every page load.
 *
 * Two cases legitimately have no markup to adopt:
 *   - the dev server, where the prerender plugin does not run (apply:'build');
 *   - the 404 documents, rendered for one URL and served at any unmatched
 *     URL, so their markup can never match. Both lack the marker. */
if (typeof document !== 'undefined') {
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element not found');

  if (document.documentElement.hasAttribute('data-pn-prerendered')) {
    hydrateRoot(rootElement, app);
  } else {
    createRoot(rootElement).render(app);
  }
}
